import Section, { SectionVisibilityProps } from './Section';
import { useTranslation } from 'react-i18next';
import TextInput from '../../inputs/TextInput';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import CheckmarkTextInput from '../../inputs/CheckmarkTextInput';
import React from 'react';
import collect from 'collect.js';
import { SectionType } from './CheckoutInfoBag';
import { Checkbox } from '@mui/material';
import useAppMethods from '../../../hooks/utility/useAppMethods';

const EMAIL_REGEX: RegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export default function InvoiceInfoSection({ show }: SectionVisibilityProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:InvoiceInfoSection' });

  const { localData, update, validationErrors } = useOrderFlow();
  const { flattenValidationErrors } = useAppMethods();

  const isFieldValid = (value: string, extraChecks?: ((value: string) => boolean)[]) =>
    value?.length > 0 && collect(extraChecks).every((callback) => callback(value));

  return (
    <Section show={show} id={SectionType.invoice_info} title={t('title')}>
      <div className="flex border p-md rounded">
        <Checkbox
          checked={localData.is_receipt_required}
          onChange={(e) => update('is_receipt_required', e.target.checked)}
          id="is-receipt-required"
        />
        <label htmlFor="is-receipt-required" className="text-sm ml-xs my-auto">
          {t('form.is_receipt_required.label')}
        </label>
      </div>
      {localData.is_receipt_required && (
        <div className="mt-md grid grid-cols-2 gap-2">
          <TextInput
            value={localData.invoice_company_name}
            onChange={(value) => update('invoice_company_name', value)}
            placeholder={t('form.company_name.placeholder')}
            inputContainerStyle="col-span-2"
          />
          <TextInput
            value={localData.invoice_first_name}
            onChange={(value) => update('invoice_first_name', value)}
            placeholder={t('form.first_name.placeholder')}
            inputContainerStyle="lg:col-span-1 col-span-2"
          />
          <TextInput
            value={localData.invoice_last_name}
            onChange={(value) => update('invoice_last_name', value)}
            placeholder={t('form.last_name.placeholder')}
            inputContainerStyle="lg:col-span-1 col-span-2"
          />
          <TextInput
            value={localData.invoice_address}
            onChange={(value) => update('invoice_address', value)}
            placeholder={t('form.address.placeholder')}
            inputContainerStyle="col-span-2"
          />
          <TextInput
            value={localData.invoice_zip_code}
            onChange={(value) => update('invoice_zip_code', value)}
            placeholder={t('form.zip_code.placeholder')}
          />
          <TextInput
            value={localData.invoice_city}
            onChange={(value) => update('invoice_city', value)}
            placeholder={t('form.city.placeholder')}
          />
          <TextInput
            value={localData.invoice_tax_id}
            onChange={(value) => update('invoice_tax_id', value)}
            placeholder={t('form.tax_id.placeholder')}
            inputContainerStyle="col-span-2"
          />
          <CheckmarkTextInput
            value={localData.invoice_email}
            isComplete={isFieldValid(localData.invoice_email, [
              (value: string) => EMAIL_REGEX.test(value.toLocaleLowerCase()),
            ])}
            onChange={(value) => update('invoice_email', value)}
            placeholder={t('form.email.placeholder')}
            classes={{
              container: 'col-span-2',
            }}
            validationErrors={flattenValidationErrors('invoice_email', validationErrors, t)}
          />
        </div>
      )}
    </Section>
  );
}
